/**
 * Effects JS helpers
 *
 * Minimal JS (~40 lines) for effects that CSS alone can't handle:
 * - 3D tilt: mouse-position-based CSS custom properties
 * - Scroll reveal: IntersectionObserver for .pms-reveal elements
 * - Ripple: click-positioned ripple span creation
 */

/** Respects prefers-reduced-motion */
function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * 3D tilt: sets --tilt-x / --tilt-y CSS vars based on mouse position.
 * Adds mousemove + mouseleave listeners to the element.
 */
export function initTilt(el: HTMLElement): void {
  if (prefersReducedMotion()) return;
  el.addEventListener('mousemove', (e: MouseEvent) => {
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    el.style.setProperty('--tilt-x', `${y}deg`);
    el.style.setProperty('--tilt-y', `${x}deg`);
  });
  el.addEventListener('mouseleave', () => {
    el.style.setProperty('--tilt-x', '0deg');
    el.style.setProperty('--tilt-y', '0deg');
  });
}

/**
 * Scroll reveal: observes .pms-reveal elements and adds --visible on intersect.
 * Each element is observed once (no re-triggering).
 *
 * @param root - ShadowRoot or HTMLElement to query within
 */
export function initScrollReveal(root: ShadowRoot | HTMLElement): void {
  if (prefersReducedMotion()) {
    // Immediately show all reveal elements
    root.querySelectorAll('.pms-reveal').forEach(el => {
      el.classList.add('pms-reveal--visible');
    });
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('pms-reveal--visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  root.querySelectorAll('.pms-reveal').forEach(el => observer.observe(el));
}

/**
 * Ripple: creates a ripple span at click position inside the element.
 * The span auto-removes after animation completes.
 */
export function createRipple(el: HTMLElement, e: MouseEvent): void {
  if (prefersReducedMotion()) return;
  const rect = el.getBoundingClientRect();
  const ripple = document.createElement('span');
  ripple.className = 'pms-ripple';
  const size = Math.max(rect.width, rect.height) * 2;
  ripple.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - rect.left - size / 2}px;top:${e.clientY - rect.top - size / 2}px`;
  el.appendChild(ripple);
  ripple.addEventListener('animationend', () => ripple.remove());
}

/**
 * Initialize effects on a rendered block container.
 * Call after a block has been rendered to the DOM.
 *
 * @param container - The block's root element
 */
export function initBlockEffects(container: HTMLElement): void {
  // Init tilt on any .pms-tilt elements
  container.querySelectorAll<HTMLElement>('.pms-tilt').forEach(initTilt);

  // Init ripple on action buttons with pms-ripple-container
  container.querySelectorAll<HTMLElement>('.pms-ripple-container').forEach(el => {
    el.addEventListener('click', (e: MouseEvent) => createRipple(el, e));
  });
}
